import "@/App.css";
import { lazy, Suspense, useEffect } from "react";
import { installTracking } from "./lib/track";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import LandingPage from "./pages/LandingPageV2";
import StrategicEnquiry from "./pages/StrategicEnquiry";
import About from "./pages/About";
import SectorPage from "./pages/SectorPage";
import Families from "./pages/Families";
import DevelopersPartners from "./pages/DevelopersPartners";
import Advisory from "./pages/Advisory";
import Access from "./pages/Access";
import MemberJoin from "./access/MemberJoin";
import MemberLogin from "./access/MemberLogin";
import { ForgotPassword, ResetPassword } from "./access/PasswordReset";
import AcademyInstitutions from "./pages/AcademyInstitutions";
import InfoPackUnsubscribe from "./pages/InfoPackUnsubscribe";
import { MemberAuthProvider } from "./access/MemberAuthContext";
import Impact from "./pages/Impact";
import Contact from "./pages/Contact";
import Experiences from "./pages/Experiences";
import ExperiencesHome from "./pages/ExperiencesHome";
import ExperiencesEvents from "./pages/ExperiencesEvents";
import Academy from "./pages/Academy";
import AcademyFlight from "./pages/AcademyFlight";
import AcademyRail from "./pages/AcademyRail";
import AcademyRegisterInterest from "./pages/AcademyRegisterInterest";
import { AdminAuthProvider } from "./admin/AdminAuthContext";
import { Toaster } from "./components/ui/sonner";
import CookieConsent from "./components/CookieConsent";
import "./styles/LandingPage.css";
import "./styles/PrivacyPages.css";
import "./styles/Consistency.css";

const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const Terms = lazy(() => import("./pages/Terms"));
const N3uroKnowledge = lazy(() => import("./pages/N3uroKnowledge"));
const InfrastructureEnquiry = lazy(() => import("./pages/InfrastructureEnquiry"));
const ParentEnquiry = lazy(() => import("./pages/ParentEnquiry"));
const MemberAccount = lazy(() => import("./access/MemberAccount"));
const SavedJourneys = lazy(() => import("./access/SavedJourneys"));
const JourneyPrep = lazy(() => import("./access/JourneyPrep"));
const Passport = lazy(() => import("./access/Passport"));
const PassportSharedView = lazy(() => import("./pages/PassportSharedView"));
const BookingConfirmed = lazy(() => import("./pages/BookingConfirmed"));
const ReschedulePage = lazy(() => import("./pages/ReschedulePage"));
const AdminLogin = lazy(() => import("./admin/AdminLogin"));
const InquiriesPage = lazy(() => import("./admin/InquiriesPage"));
const StrategicEnquiriesPage = lazy(() => import("./admin/StrategicEnquiriesPage"));
const InfrastructureEnquiriesPage = lazy(() => import("./admin/InfrastructureEnquiriesPage"));
const ParentEnquiriesPage = lazy(() => import("./admin/ParentEnquiriesPage"));
const PaidBookingsPage = lazy(() => import("./admin/PaidBookingsPage"));
const BlackoutsPage = lazy(() => import("./admin/BlackoutsPage"));
const AccessLeadsPage = lazy(() => import("./admin/AccessLeadsPage"));
const ExperiencesEnquiriesPage = lazy(() => import("./admin/ExperiencesEnquiriesPage"));
const ConversionPage = lazy(() => import("./admin/ConversionPage"));
const AcademyInterestPage = lazy(() => import("./admin/AcademyInterestPage"));
const N3uropediaAdminPage = lazy(() => import("./admin/N3uropediaAdminPage"));
const N3uropedia = lazy(() => import("./pages/N3uropedia"));
const N3uropediaCollection = lazy(() => import("./pages/N3uropediaCollection"));
const N3uropediaArticle = lazy(() => import("./pages/N3uropediaArticle"));
const Discovery = lazy(() => import("./access/Discovery"));
const JourneysGuide = lazy(() => import("./access/JourneysGuide"));
const WhatIsN3uroWorld = lazy(() => import("./pages/WhatIsN3uroWorld"));
const AcademyLesson = lazy(() => import("./pages/AcademyLesson"));
const AcademyAdminPage = lazy(() => import("./admin/AcademyAdminPage"));
const ChecklistSharedView = lazy(() => import("./pages/ChecklistSharedView"));

const AZ = "/n3uropedia/neurodiversity-a-z";
const KnowledgeRedirect = () => {
  const slug = window.location.hash.slice(1);
  return <Navigate to={slug ? `${AZ}/${slug}` : AZ} replace />;
};
const TermRedirect = () => <Navigate to={`${AZ}/${useParams().slug}`} replace />;
const RouteFallback = () => <div style={{ minHeight: "100vh", background: "#0F0A1E" }} />;

function App() {
  useEffect(() => installTracking(), []);
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <AdminAuthProvider>
            <MemberAuthProvider>
            <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/about" element={<About />} />
              <Route path="/what-is-n3uro-world" element={<WhatIsN3uroWorld />} />
              <Route path="/sectors/:slug" element={<SectorPage />} />
              <Route path="/our-model" element={<Navigate to="/about#model" replace />} />
              <Route path="/families" element={<Families />} />
              <Route path="/developers-partners" element={<DevelopersPartners />} />
              <Route path="/advisory" element={<Advisory />} />
              <Route path="/n3uro-advisory" element={<Advisory />} />
              <Route path="/access" element={<Access />} />
              <Route path="/n3uro-access" element={<Access />} />
              <Route path="/access/join" element={<MemberJoin />} />
              <Route path="/access/sign-in" element={<MemberLogin />} />
              <Route path="/access/account" element={<MemberAccount />} />
              <Route path="/access/forgot-password" element={<ForgotPassword />} />
              <Route path="/access/reset-password" element={<ResetPassword />} />
              <Route path="/access/journeys" element={<SavedJourneys />} />
              <Route path="/access/journey-prep" element={<JourneyPrep />} />
              <Route path="/access/passport" element={<Passport />} />
              <Route path="/impact" element={<Impact />} />
              <Route path="/experiences" element={<Experiences />} />
              <Route path="/experiences/home" element={<ExperiencesHome />} />
              <Route path="/experiences/events" element={<ExperiencesEvents />} />
              <Route path="/academy" element={<Academy />} />
              <Route path="/academy/flight" element={<AcademyFlight />} />
              <Route path="/academy/rail" element={<AcademyRail />} />
              <Route path="/academy/register-interest" element={<AcademyRegisterInterest />} />
              <Route path="/academy/institutions" element={<AcademyInstitutions />} />
              <Route path="/academy/info-pack/unsubscribe" element={<InfoPackUnsubscribe />} />
              <Route path="/academy/learn/:code/:module" element={<AcademyLesson />} />
              <Route path="/organisations" element={<DevelopersPartners />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/strategic-alignment" element={<Navigate to="/infrastructure-enquiry" replace />} />
              <Route path="/n3uro-knowledge" element={<KnowledgeRedirect />} />
              <Route path="/language" element={<KnowledgeRedirect />} />
              <Route path="/glossary" element={<KnowledgeRedirect />} />
              <Route path="/knowledge" element={<KnowledgeRedirect />} />
              <Route path="/n3uropedia" element={<N3uropedia />} />
              <Route path="/n3uropedia/:collection" element={<N3uropediaCollection />} />
              <Route path="/n3uropedia/:collection/:slug" element={<N3uropediaArticle />} />
              <Route path="/access/discovery" element={<Discovery />} />
              <Route path="/access/journeys-guide" element={<JourneysGuide />} />
              <Route path="/knowledge/legacy" element={<N3uroKnowledge />} />
              <Route path="/knowledge/:slug" element={<TermRedirect />} />
              <Route path="/passport/view/:token" element={<PassportSharedView />} />
              <Route path="/checklist/view/:token" element={<ChecklistSharedView />} />
              <Route path="/strategic-enquiry" element={<StrategicEnquiry />} />
              <Route path="/infrastructure-enquiry" element={<InfrastructureEnquiry />} />
              <Route path="/parent-enquiry" element={<ParentEnquiry />} />
              <Route path="/booking-confirmed" element={<BookingConfirmed />} />
              <Route path="/reschedule" element={<ReschedulePage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/admin" element={<AdminLogin />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin/inquiries" element={<InquiriesPage />} />
              <Route path="/admin/strategic-enquiries" element={<StrategicEnquiriesPage />} />
              <Route path="/admin/infrastructure-enquiries" element={<InfrastructureEnquiriesPage />} />
              <Route path="/admin/parent-enquiries" element={<ParentEnquiriesPage />} />
              <Route path="/admin/paid-bookings" element={<PaidBookingsPage />} />
              <Route path="/admin/blackouts" element={<BlackoutsPage />} />
              <Route path="/admin/access-leads" element={<AccessLeadsPage />} />
              <Route path="/admin/experiences-enquiries" element={<ExperiencesEnquiriesPage />} />
              <Route path="/admin/conversion" element={<ConversionPage />} />
              <Route path="/admin/academy-interest" element={<AcademyInterestPage />} />
              <Route path="/admin/n3uropedia" element={<N3uropediaAdminPage />} />
              <Route path="/admin/academy" element={<AcademyAdminPage />} />
            </Routes>
            </Suspense>
            <CookieConsent />
            </MemberAuthProvider>
          </AdminAuthProvider>
        </BrowserRouter>
        <Toaster position="top-right" />
      </div>
    </HelmetProvider>
  );
}

export default App;
